/**
 * In-memory request cache with TTL.
 *
 * Used by the adapter layer to avoid repeat identical Overpass/ORM
 * requests within a browser session.
 *
 * TTL: 5 minutes (configurable).
 * Key: SHA-256-like FNV-32 hash of the query string (good-enough for a session cache).
 * No localStorage — keeps the implementation simple and avoids user data retention.
 */

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry<T> {
  data: T;
  expiresAt: number;
}

export class RequestCache<T = unknown> {
  private store = new Map<string, CacheEntry<T>>();
  private readonly ttl: number;

  constructor(ttlMs = DEFAULT_TTL_MS) {
    this.ttl = ttlMs;
  }

  get(key: string): T | undefined {
    const entry = this.store.get(key);
    if (!entry) return undefined;
    if (Date.now() > entry.expiresAt) {
      this.store.delete(key);
      return undefined;
    }
    return entry.data;
  }

  set(key: string, data: T): void {
    this.store.set(key, { data, expiresAt: Date.now() + this.ttl });
  }

  has(key: string): boolean {
    return this.get(key) !== undefined;
  }

  delete(key: string): void {
    this.store.delete(key);
  }

  clear(): void {
    this.store.clear();
  }

  get size(): number {
    // Only count non-expired entries
    let count = 0;
    for (const [key, entry] of this.store) {
      if (Date.now() <= entry.expiresAt) count++;
      else this.store.delete(key);
    }
    return count;
  }
}

/**
 * FNV-32a hash — fast, good-enough key for a session cache.
 * Not cryptographic; not suitable for security use.
 */
export function cacheKey(query: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < query.length; i++) {
    hash ^= query.charCodeAt(i);
    hash = (hash * 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

/** Singleton overpass cache for the browser session */
export const overpassCache = new RequestCache<unknown>();

/** Singleton ORM cache for the browser session */
export const ormCache = new RequestCache<unknown>();
